import { api } from '../../api/client';

// ── server DTOs (match Application/Common/Interfaces/IQcBatchReader.cs +
//    Application/Analysis/QcBatch/QcSequenceBuilder.cs) ──────────────────────
export interface QcJob        { jobOid: string; jobID: string | null; receivedDate: string | null; dueDate: string | null; tat: string | null; sampleCount: number }
// Fields after labwareID are populated only for the Others-tab "Selected"
// summary grid (AI_Analysis_QcBatch_LinkedInstruments_SP) — the header-field
// picker and Add-popup catalog don't select them, so they're undefined there.
export interface QcInstrument {
  instrumentOid: string; instrumentName: string | null; labwareName: string | null; labwareID?: string | null;
  manufacturer?: string | null; expirationDate?: string | null; departmentUsedName?: string | null;
  active?: boolean; maintenanceFrequency?: string | null; departmentNames?: string | null;
  categoryName?: string | null; retiredDate?: string | null;
}
export interface QcTemplate   { templateID: number; templateName: string | null }
export interface QcPrepBatch  { prepBatchOid: string; prepBatchID: string | null; createdDate: string | null; createdBy: string | null; roomTemp: string | null; humidity: string | null; instrumentOid: string | null; instrumentName: string | null }
export interface QcTypeOption { qcTypeOid: string; qcTypeName: string | null; sequenceOrder: number | null; sort: number | null; qcSource: string | null; qcRole: string | null; qcRootRole: string | null; panelVisible: boolean }

// Reagents (Others tab) — legacy ReagentLink_Execute parity.
export interface QcLinkedReagent {
  oid: string; reagentID: string | null; reagentName: string | null; prepType: string | null;
  expiredDate: string | null; vendorLT: string | null; preparedBy: string | null;
  datePrepared: string | null; dateReceived: string | null; vendorName: string | null;
  // True only for an LT (not LLT) reagent with an uploaded vendor cert on file —
  // gates whether the grid shows a clickable COA link (legacy hides it otherwise).
  hasCoa: boolean;
}
export interface QcAvailableReagent {
  reagentID: string | null; reagentName: string | null; prepType: string | null;
  expiryDate: string | null; test: string | null; datePrepared: string | null;
  preparedBy: string | null; division: string | null; department: string | null;
  createdDate: string | null; dateReceived: string | null; vendor: string | null; vendorLT: string | null;
}

export interface QcSample {
  sampleLogInOid: string;
  sysSampleCode:  string | null;
  jobID:          string | null;
  component:      string | null;
  clientSampleID: string | null;
  collectDate:    string | null;
  sampleNo:       number;
  // legacy QC Batch grid columns (post-Load, pre-Sort)
  dilutionCount:  number;
  finalVolMl:     string | null;
  // Analyst-entered pre-Sort (no source column); sent back on Sort so the built
  // sequence retains it — set from the current grid row in buildSequence().
  sampleAmount:   string | null;
  excludedReport: boolean;
  units:          string | null;
  sampleName:     string | null;
  siteName:       string | null;
  samplingAddress: string | null;
  dateCreated:    string | null;
  dateReceived:   string | null;
  dateCollected:  string | null;
  client:         string | null;
  projectID:      string | null;
  projectName:    string | null;
  holdTime:       string | null;
  // true when this sample still needs sample-prep before it can be sorted into a
  // QC batch (legacy prep-batch gate) — surfaced as a Sort-time validation.
  requiresPrep:   boolean;
}

export interface QcSeqRow {
  // The persisted QCBatchSequence.Oid for a row read from a SAVED batch (null for
  // a freshly-built or freshly-added row) — round-tripped so the reconcile-on-Save
  // update (A1) can diff existing vs added rows. Survives Add/Remove/Move/Dilution
  // (the server builders shallow-copy the list + mutate existing instances in place).
  seqOid?:     string | null;
  kind:        'sample' | 'qc' | 'dilution';
  qcType:      string;
  qcTypeOid:   string | null;
  sort:        number;
  runno:       number;
  code:        string;
  // NULL on Sample/sample-dilution rows; the SHORT run code ("Dup1"/"MS1") on
  // sourced QC rows — the legacy persisted shape (live-data verified).
  systemId:    string | null;
  jobId:       string | null;
  component:   string | null;
  strSampleId: string | null;
  sampleId:    string | null;
  sampleLogInOid: string | null;
  df:          number;
  sampleAmount: string | null;
  finalVol:    string | null;
  dupSource:   string | null;
  spikeSource: string | null;
  sourceId:    string | null;
  // Dilution provenance (parent run's code, dilution rows only) + reported-run
  // flag (legacy QCBatchSequence.OriginalSYSSamplecode / IsReport).
  originalSysSampleCode: string | null;
  isReport:    boolean;
  // Legacy-parity lookup columns carried through Sort (blank on pure QC rows).
  dilutionCount:   number;
  excludedReport:  boolean;
  units:           string | null;
  sampleName:      string | null;
  siteName:        string | null;
  samplingAddress: string | null;
  dateCreated:     string | null;
  dateReceived:    string | null;
  dateCollected:   string | null;
  client:          string | null;
  projectId:       string | null;
  projectName:     string | null;
  holdTime:        string | null;
}

// Reagents/instruments picked BEFORE the batch existed — legacy parity
// (ReagentLink/InstrumentLink are gated by user permission only, not by whether
// the batch is saved); persisted atomically with Create.
export interface QcStagedReagent {
  reagentId: string; reagentName: string; prepType: string | null;
  expiredDate: string | null; vendorLT: string | null; preparedBy: string | null;
  datePrepared: string | null; dateReceived: string | null; vendorOid: string | null;
}

export interface QcCreateBody {
  testMethodId:   string;
  templateId:     number | null;
  jobIds:         string | null;     // ';'-delimited job-id labels
  instrumentOid:  string | null;
  instrumentName: string | null;
  noRuns:         number;
  roomTemp:       string | null;
  humidity:       string | null;
  comments:       string | null;
  sequence:       QcSeqRow[];
  stagedReagents?:        QcStagedReagent[];
  stagedInstrumentOids?:  string[];
}
// One prior QC batch in the "Copy From Previous Batch" picker.
export interface QcPreviousBatch {
  abOid: string; qcBatchId: string | null; createdDate: string | null;
  jobIds: string | null; status: number; statusText: string | null; matrix: string | null;
  reagentCount: number; instrumentCount: number;
}

export interface QcCreateResult { qcBatchId: string; abOid: string; seqCount: number; samplesLinked: number }

/** Body for the SDMS merge-data fetch (Ok → SDMS handoff): the picked template +
 *  the built sequence (expanded to sample×parameter records server-side). Header
 *  FIELD values are filled client-side from the seed. */
export interface SdmsMergeDataBody {
  templateId:   number;
  testMethodId: string;
  sequence:     QcSeqRow[];
}
export interface QcRollbackResult { qcBatchId: string; oldStatus: number; affectedSamples: number }

// "Open an existing QC batch" (view/edit mode) — matches QcBatchHeaderRow
// (Application/Common/Interfaces/IQcBatchReader.cs). jobIds/instrumentOids are
// the RAW stored strings (';'-joined display labels for jobs, Oid string(s)
// for instrument) — not resolved back through the picker lists.
export interface QcBatchHeader {
  abOid: string; qcBatchId: string | null; createdDate: string | null; createdByName: string | null;
  testMethodId: string; test: string | null; matrix: string | null; method: string | null;
  jobIds: string | null; jobOids: string | null; instrumentOids: string | null; instrumentName: string | null;
  noRuns: number | null; roomTemp: string | null; humidity: string | null; comments: string | null;
  status: number; statusText: string | null; templateId: number | null; prepBatchId: string | null;
  rolledBackBy: string | null; rolledBackDate: string | null; rollbackReason: string | null;
  reviewedBy: string | null; reviewedDate: string | null; verifiedBy: string | null; verifiedDate: string | null;
}
export interface QcBatchDetail { header: QcBatchHeader; sequence: QcSeqRow[] }

// QC Batches list "New" — cascading Matrix → Test → Method picker rows
// (QcBatchPickMatrixRow / QcBatchPickTestRow in IQcBatchReader.cs).
// One (sample × analyte) result row for the SDMS "QC Batch Results" grid — a
// focused subset of the backend BatchResultRow (camelCased JSON). The endpoint
// returns ~35 fields; these are the ones the read-only results view shows.
export interface SdmsBatchResult {
  id: string | null;
  sys: string | null; parameter: string | null;
  numericResult: string | null; result: string | null; units: string | null;
  analyzedDate: string | null; analyzedBy: string | null;
  df: string | null; rptLimit: string | null; mdl: string | null; loq: string | null;
  spikeAmount: string | null; sampleName: string | null; client: string | null; projectId: string | null;
}

export interface QcPickMatrix { matrixOid: string; matrixName: string | null }
export interface QcPickTest   { testMethodId: string; testName: string | null; methodNumber: string | null }

// SDMS ribbon grid lookups. OG's visible columns are Test → Test·Matrix·Method·
// TemplateName·Sx ("Sx" = count of DISTINCT pending samples for that test, NOT a
// code), and QCBatchID → JobID·Test·Matrix·Method·Template·QCBatchID(ABID)·Status
// (Status shown in View mode only). `diviion` keeps the legacy sproc's spelling.
export interface SdmsTestLookup {
  test: string | null; matrix: string | null; method: string | null;
  templateName: string | null; sx: number; testMethodId: string; templateID: number;
}
export interface SdmsQcBatchLookup {
  uqAnalyticalBatchID: string; uqCalibrationID: number; templateID: number; uqTestMethodID: string;
  jobID: string | null; test: string | null; matrix: string | null; method: string | null;
  template: string | null; abid: string | null; status: string | null; diviion: string | null;
}

interface Envelope<T> { success: boolean; data?: T; error?: { code: string; message: string } }

const unwrap = <T,>(d: Envelope<T>): T => {
  if (!d.success || d.data === undefined) throw new Error(d.error?.message ?? 'Request failed.');
  return d.data;
};

const BASE = '/api/v1/analysis/qc-batches';

export const qcBatchApi = {
  // QC Batches list "New" — cascading Matrix → Test → Method pickers (OG
  // parity: legacy New opens the create dashboard with NO test; each lookup is
  // gated on the previous pick). The Method pick is the definitive TestMethod.
  newPickerMatrices: () =>
    api.get<Envelope<QcPickMatrix[]>>(`${BASE}/new-picker/matrices`).then(r => unwrap(r.data)),
  newPickerTests: (matrixOid: string) =>
    api.get<Envelope<QcPickTest[]>>(`${BASE}/new-picker/tests`, { params: { matrixOid } }).then(r => unwrap(r.data)),
  /** The batch's SDMS workbook (.xlsx bytes) — its SAVED sheet when present, else
   *  the empty template for its TemplateID, else null on 204 ("nothing on file").
   *  Reuses the endpoint the Result-Validation preview already uses, so Result
   *  Entry and the preview read the same blob. */
  sdmsWorkbook: async (batchOid: string) => {
    const res = await api.get(
      `/api/v1/analysis/result-validation/${encodeURIComponent(batchOid)}/sdms-workbook`,
      { responseType: 'arraybuffer' });
    const buf = res.data as ArrayBuffer;
    if (res.status === 204 || !buf || buf.byteLength === 0) return null;
    return {
      buf,
      source:       String(res.headers?.['x-sdms-source'] ?? ''),
      templateName: String(res.headers?.['x-sdms-template-name'] ?? ''),
    };
  },

  /** SDMS Result ENTRY — persist the analyst's typed results by saving the edited
   *  workbook blob against the batch (legacy SpreadsheetEntryController's
   *  `ABID.SpreadSheet = …SaveDocument(OpenXml)`).  Raw .xlsx body, NOT the
   *  multipart shape the Result-VALIDATION save uses: entry has no per-cell
   *  `changes` part, audits under its own FormName, and must not run validation's
   *  self-review gate.  The saved blob is what `sdmsWorkbook` serves back on
   *  reopen, so the typed values survive. */
  saveSdmsEntryWorkbook: (batchOid: string, bytes: ArrayBuffer) =>
    api.put<Envelope<{ bytes: number }>>(
      `/api/v1/analysis/sdms/${encodeURIComponent(batchOid)}/workbook`,
      bytes,
      { headers: { 'Content-Type': 'application/octet-stream' } },
    ).then(r => unwrap(r.data)),

  /** SDMS merge INPUTS for the Ok → SDMS handoff (batch not yet saved): the per-test
   *  template (bytes) + the sequence expanded to sample×parameter records. The merge
   *  itself runs client-side (ExcelJS, so the result stays Univer-readable). Null on
   *  204 (template has no blob). NB: SDMS endpoints live on AnalysisController
   *  (/api/v1/analysis/…), NOT under BASE (/api/v1/analysis/qc-batches). */
  sdmsMergeData: async (body: SdmsMergeDataBody) => {
    const res = await api.post<Envelope<{ templateBase64: string; templateName: string | null; records: Record<string, unknown>[] }>>(
      '/api/v1/analysis/sdms/merge-data', body);
    if (res.status === 204) return null;
    const data = unwrap(res.data);
    if (!data?.templateBase64) return null;
    const bin = atob(data.templateBase64);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    return { template: bytes.buffer, templateName: data.templateName ?? '', records: data.records ?? [] };
  },

  /** Read-only (sample × analyte) results for a batch — the SDMS "QC Batch
   *  Results" grid. Reuses the endpoint the Result-Validation package view reads
   *  (dbo.SpreadSheetEntry_SampleInfo ⋈ SampleParameter/SampleLogIn). */
  sdmsBatchResults: (batchOid: string) =>
    api.get<Envelope<SdmsBatchResult[]>>(
      `/api/v1/analysis/result-validation/${encodeURIComponent(batchOid)}/batch-results`,
    ).then(r => unwrap(r.data)),

  // SDMS ribbon grid lookups (mode = the ribbon's Mode combo: 'Enter' | 'View').
  sdmsTests: (mode: string) =>
    api.get<Envelope<SdmsTestLookup[]>>(`${BASE}/sdms/tests`, { params: { mode } }).then(r => unwrap(r.data)),
  /** `testMethodId` is OPTIONAL: omitting it is OG's View-mode call (`EXEC … @Mode`
   *  with no test), which lists every test's batches. Enter mode does need one —
   *  its legacy branch has no null-guard on the test, so it returns nothing without. */
  sdmsQcBatches: (testMethodId: string | undefined, mode: string) =>
    api.get<Envelope<SdmsQcBatchLookup[]>>(`${BASE}/sdms/qc-batches`,
      { params: { testMethodId: testMethodId || undefined, mode } }).then(r => unwrap(r.data)),

  newPickerMethods: (testMethodId: string) =>
    api.get<Envelope<QcPickTest[]>>(`${BASE}/new-picker/methods`, { params: { testMethodId } }).then(r => unwrap(r.data)),

  jobs: (testMethodId: string) =>
    api.get<Envelope<QcJob[]>>(`${BASE}/jobs`, { params: { testMethodId } }).then(r => unwrap(r.data)),

  instruments: (testMethodId: string) =>
    api.get<Envelope<QcInstrument[]>>(`${BASE}/instruments`, { params: { testMethodId } }).then(r => unwrap(r.data)),

  templates: (testMethodId: string) =>
    api.get<Envelope<QcTemplate[]>>(`${BASE}/templates`, { params: { testMethodId } }).then(r => unwrap(r.data)),

  prepBatches: (testMethodId: string) =>
    api.get<Envelope<QcPrepBatch[]>>(`${BASE}/prep-batches-for-test`, { params: { testMethodId } }).then(r => unwrap(r.data)),

  qcTypes: (testMethodId: string) =>
    api.get<Envelope<QcTypeOption[]>>(`${BASE}/qc-types`, { params: { testMethodId } }).then(r => unwrap(r.data)),

  load: (testMethodId: string, jobOids: string[]) =>
    api.post<Envelope<QcSample[]>>(`${BASE}/load`, { testMethodId, jobOids }).then(r => unwrap(r.data)),

  // Import a Sample Prep Batch's prepared samples (same shape as `load`) — legacy
  // "select a Prep Batch" path; the rows then go through Sort + Create unchanged.
  loadFromPrepBatch: (testMethodId: string, prepBatchOid: string) =>
    api.post<Envelope<QcSample[]>>(`${BASE}/load-from-prep-batch`, { testMethodId, prepBatchOid }).then(r => unwrap(r.data)),

  // OG-exact prep import: the prep batch's already-built sequence (samples + QC
  // interleave) as QcSeqRow[], so the grid opens SORTED (verbatim copy).
  seqFromPrepBatch: (prepBatchOid: string) =>
    api.post<Envelope<QcSeqRow[]>>(`${BASE}/seq-from-prep-batch`, { prepBatchOid }).then(r => unwrap(r.data)),

  buildSequence: (testMethodId: string, templateId: number | null, noRuns: number, samples: QcSample[]) =>
    api.post<Envelope<QcSeqRow[]>>(`${BASE}/build-sequence`,
      { testMethodId, templateId, noRuns, samples }).then(r => unwrap(r.data)),

  create: (body: QcCreateBody) =>
    api.post<Envelope<QcCreateResult>>(`${BASE}/create`, body).then(r => unwrap(r.data)),

  // Revert a saved batch to Pending Result Entry (legacy QCBatchRollback parity).
  rollback: (abOid: string, reason: string) =>
    api.post<Envelope<QcRollbackResult>>(`${BASE}/${abOid}/rollback`, { reason }).then(r => unwrap(r.data)),

  // "Open an existing QC batch" (view/edit mode) — header + built sequence.
  getDetail: (abOid: string) =>
    api.get<Envelope<QcBatchDetail>>(`${BASE}/${abOid}/detail`).then(r => unwrap(r.data)),

  // Persist edits to a saved batch (A1 — reconcile-on-Save). Posts the FULL
  // current grid (rows with seqOid = existing, without = added; any stored row
  // whose Oid is absent was removed) + Room Temp/Humidity.
  updateBatch: (abOid: string, body: { roomTemp: string | null; humidity: string | null; comments: string | null; sequence: QcSeqRow[] }) =>
    api.put<Envelope<{ added: number; removed: number; updated: number }>>(`${BASE}/${abOid}`, body)
      .then(r => unwrap(r.data)),

  // Delete a saved QC batch (legacy DeleteAction parity — cascade + return samples to
  // the queue). Guard errors (non-deletable status / reported / results entered) come
  // back as a rejected promise with the friendly message.
  deleteBatch: (abOid: string) =>
    api.delete<Envelope<{ qcBatchId: string }>>(`${BASE}/${abOid}`).then(r => unwrap(r.data)),

  // Instant per-row ExcludedReport toggle on a saved batch (legacy :5595 — the one
  // true per-action write; persists SampleParameter.NotReport immediately so it is
  // not lost if the user navigates away without a full Save).
  setExcludedReport: (abOid: string, seqOid: string, excluded: boolean) =>
    api.put<Envelope<{ affected: number }>>(
      `${BASE}/${abOid}/sequence/${seqOid}/excluded-report`, { excluded },
      { silentLoading: true } as never).then(r => unwrap(r.data)),

  // Prior QC batches for the "Copy From Previous Batch" picker (same test method,
  // excluding the current one).
  previousBatches: (testMethodId: string, excludeAbOid?: string) =>
    api.get<Envelope<QcPreviousBatch[]>>(`${BASE}/previous`,
      { params: { testMethodId, excludeAbOid } }).then(r => unwrap(r.data)),

  // Reagents (Others tab) — legacy ReagentLink_Execute parity.
  linkedReagents: (abOid: string) =>
    api.get<Envelope<QcLinkedReagent[]>>(`${BASE}/${abOid}/reagents`).then(r => unwrap(r.data)),

  /** "Add Reagents" picker catalog. `testMethodId` scopes it to the batch's test —
   *  without it the picker listed every reagent in the lab (VOC/TOC preparations on
   *  an Alkalinity batch; user report 2026-08-07). */
  availableReagents: (testMethodId?: string | null) =>
    api.get<Envelope<QcAvailableReagent[]>>(`${BASE}/reagents/available`,
      testMethodId ? { params: { testMethodId } } : undefined).then(r => unwrap(r.data)),

  linkReagent: (abOid: string, reagent: QcAvailableReagent) =>
    api.post<Envelope<{ reagentOid: string }>>(`${BASE}/${abOid}/reagents`, {
      reagentId: reagent.reagentID, reagentName: reagent.reagentName, prepType: reagent.prepType,
      expiredDate: reagent.expiryDate, vendorLT: reagent.vendorLT, preparedBy: reagent.preparedBy,
      datePrepared: reagent.datePrepared, dateReceived: reagent.dateReceived, vendorOid: null,
    }).then(r => unwrap(r.data)),

  unlinkReagent: (abOid: string, reagentOid: string, reagentId: string | null) =>
    api.delete<Envelope<{ unlinked: boolean }>>(`${BASE}/${abOid}/reagents/${reagentOid}`, { params: { reagentId } }).then(r => unwrap(r.data)),

  // COA (Certificate of Analysis) — "LT"-prefixed reagent lots only (legacy
  // PreviewCOA_Execute; "LLT" dynamic-report generation is out of scope). OPENS
  // the certificate in a NEW BROWSER TAB (legacy ShowPdfInNewTab parity; was a
  // forced download before). Fetched as an authenticated blob — a bare <a>/window
  // .open on the URL can't carry the JWT this endpoint requires — then the blob
  // URL is opened in a new tab, where the browser renders the inline PDF.
  openReagentCoa: async (reagentId: string) => {
    const res = await api.get<Blob>(`${BASE}/reagents/${encodeURIComponent(reagentId)}/coa`, { responseType: 'blob' });
    const blob = res.data instanceof Blob ? res.data : new Blob([res.data as BlobPart], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank', 'noopener,noreferrer');
    // Keep the object URL alive long enough for the new tab to load it.
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  },

  // Instruments (Others tab) — legacy InstrumentLink_Execute parity. A DIFFERENT
  // catalog from `instruments()` above (that one is test-method-scoped, for the
  // single header field; this is ALL active instruments, legacy parity).
  linkedInstruments: (abOid: string) =>
    api.get<Envelope<QcInstrument[]>>(`${BASE}/${abOid}/instruments`).then(r => unwrap(r.data)),

  /** Add-Instrument picker catalog. `testMethodId` scopes it to the instruments
   *  linked to that test on the legacy "Instruments, Lab Analysis" screen. */
  availableInstruments: (testMethodId?: string | null) =>
    api.get<Envelope<QcInstrument[]>>(`${BASE}/instruments/available`,
      testMethodId ? { params: { testMethodId } } : undefined).then(r => unwrap(r.data)),

  linkInstrument: (abOid: string, instrumentOid: string, instrumentName: string | null) =>
    api.post<Envelope<{ linked: boolean }>>(`${BASE}/${abOid}/instruments/${instrumentOid}`, null,
      { params: { instrumentName } }).then(r => unwrap(r.data)),

  unlinkInstrument: (abOid: string, instrumentOid: string, instrumentName: string | null) =>
    api.delete<Envelope<{ unlinked: boolean }>>(`${BASE}/${abOid}/instruments/${instrumentOid}`,
      { params: { instrumentName } }).then(r => unwrap(r.data)),

  // Add Samples to a saved batch — legacy AddQCBatchSample_* parity.
  addSampleCandidates: (abOid: string) =>
    api.get<Envelope<QcSample[]>>(`${BASE}/${abOid}/add-sample-candidates`).then(r => unwrap(r.data)),

  addSamples: (abOid: string, sampleLogInOids: string[]) =>
    api.post<Envelope<{ seqAdded: number; samplesLinked: number }>>(`${BASE}/${abOid}/add-samples`,
      { sampleLogInOids }).then(r => unwrap(r.data)),

  // Manually insert a QC row under a selected parent row (legacy QCadd_Execute
  // parity) — stateless: posts the CURRENT full sequence, returns the recomputed
  // one (naming + same-type renumbering applied server-side).
  addQc: (testMethodId: string, parentSort: number, newQcTypeOid: string, sequence: QcSeqRow[]) =>
    api.post<Envelope<QcSeqRow[]>>(`${BASE}/add-qc`,
      { testMethodId, parentSort, newQcTypeOid, sequence }).then(r => unwrap(r.data)),

  // Remove a row from the sequence (legacy QCremove_Execute parity, minus the
  // no-op "canremove" check — see QcSequenceBuilder.RemoveQc).
  removeQc: (testMethodId: string, removeSort: number, sequence: QcSeqRow[]) =>
    api.post<Envelope<QcSeqRow[]>>(`${BASE}/remove-qc`,
      { testMethodId, removeSort, sequence }).then(r => unwrap(r.data)),

  // Drag-drop reorder (legacy DRAGROW parity) — server validates the drop against
  // each row's allowed Sort window and returns the resequenced list.
  moveRow: (testMethodId: string, dragSort: number, targetSort: number, sequence: QcSeqRow[]) =>
    api.post<Envelope<QcSeqRow[]>>(`${BASE}/move-row`,
      { testMethodId, dragSort, targetSort, sequence }).then(r => unwrap(r.data)),

  // "Add Dilution" context-menu action (legacy parity) — adds one R replicate to the
  // selected non-dilution row; server derives the name + placement and resequences.
  addDilution: (testMethodId: string, parentSort: number, sequence: QcSeqRow[]) =>
    api.post<Envelope<QcSeqRow[]>>(`${BASE}/add-dilution`,
      { testMethodId, parentSort, sequence }).then(r => unwrap(r.data)),
};
